import { useEffect, useRef, useState, type HTMLAttributes, type KeyboardEvent, type ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from 'lucide-react'
import { initials } from '@spark/shared'
import { cx } from './cx.ts'

export function Card({ className, children, pad = true, ...rest }: HTMLAttributes<HTMLDivElement> & { pad?: boolean }) {
  return (
    <div className={cx('card', pad && 'p-4 sm:p-5', className)} {...rest}>
      {children}
    </div>
  )
}

export function CardTitle({ children, actions, className }: { children: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cx('mb-3 flex flex-wrap items-center justify-between gap-2', className)}>
      <h2 className="display text-[20px] sm:text-[22px]">{children}</h2>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('label-caps', className)}>{children}</div>
}

export type PillTone = 'neutral' | 'forest' | 'gold' | 'live' | 'ok' | 'warn' | 'ink' | 'danger'

const pillTones: Record<PillTone, string> = {
  neutral: 'bg-mist text-text',
  forest: 'bg-forest text-white',
  gold: 'bg-gold-bright text-ink',
  live: 'bg-live text-white',
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-gold-deep',
  ink: 'bg-navsurf text-cream',
  danger: 'bg-live-soft text-live',
}

export function Pill({ tone = 'neutral', children, className }: { tone?: PillTone; children: ReactNode; className?: string }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-[3px] text-[10.5px] font-bold tracking-[0.06em] whitespace-nowrap uppercase',
        pillTones[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

const statusTones: Record<string, [PillTone, string]> = {
  live: ['live', 'Live'],
  final: ['neutral', 'Final'],
  scheduled: ['neutral', 'Scheduled'],
  forfeit: ['warn', 'Forfeit'],
  cancelled: ['danger', 'Cancelled'],
  postponed: ['warn', 'Postponed'],
  next: ['warn', 'Up next'],
  now: ['live', 'Now'],
  done: ['neutral', 'Done'],
  waiting: ['neutral', 'Waiting'],
  absent: ['danger', 'Absent'],
  open: ['ok', 'Active'],
  pending: ['warn', 'Pending'],
  paid: ['ok', 'Paid'],
  completed: ['ok', 'Paid'],
  sent: ['ok', 'Sent'],
  queued: ['warn', 'Sending'],
  sending: ['warn', 'Sending'],
  scheduled_send: ['warn', 'Scheduled'],
  failed: ['danger', 'Failed'],
  draft: ['neutral', 'Draft'],
  approved: ['ok', 'In the app'],
  submitted: ['warn', 'Waiting for review'],
  link_sent: ['neutral', 'Link sent'],
  none: ['neutral', 'No profile'],
  changes_requested: ['warn', 'Changes requested'],
  needs_review: ['danger', 'Needs review'],
  checked_in: ['ok', 'Checked in'],
  partial: ['warn', 'In progress'],
  removed: ['danger', 'Removed on website'],
  inactive: ['danger', 'Off'],
}

export function StatusPill({ status, label }: { status: string; label?: string }) {
  const [tone, text] = statusTones[status] ?? ['neutral', status]
  return <Pill tone={tone}>{label ?? text}</Pill>
}

export function Avatar({ name, size = 36, tone = 'forest', src }: { name: string; size?: number; tone?: 'forest' | 'gold' | 'ink'; src?: string }) {
  const bg = tone === 'gold' ? 'bg-gold-deep' : tone === 'ink' ? 'bg-navsurf' : 'bg-forest'
  if (src) {
    return <img src={src} alt="" width={size} height={size} className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />
  }
  return (
    <span
      aria-hidden
      className={cx('display inline-flex shrink-0 items-center justify-center rounded-full text-white', bg)}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
    >
      {initials(name)}
    </span>
  )
}

export function Kpi({ value, label, tone = 'forest', hint }: { value: ReactNode; label: string; tone?: 'forest' | 'live' | 'gold'; hint?: ReactNode }) {
  const color = tone === 'live' ? 'text-live' : tone === 'gold' ? 'text-gold-deep' : 'text-forest'
  return (
    <div className="card flex flex-col gap-1 px-4 py-3.5">
      <div className={cx('display num text-[28px] sm:text-[30px]', color)}>{value}</div>
      <div className="label-caps">{label}</div>
      {hint && <div className="text-[12px] text-muted">{hint}</div>}
    </div>
  )
}

export function Spinner({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <div role="status" className={cx('flex items-center gap-2 text-[13px] text-muted', className)}>
      <Loader2 className="size-4 animate-spin" aria-hidden />
      <span>{label}…</span>
    </div>
  )
}

export function EmptyState({
  icon,
  title,
  children,
  action,
  titleAs: Title = 'div',
}: {
  icon?: ReactNode
  title: string
  children?: ReactNode
  action?: ReactNode
  /** Heading element for the title when the empty state is the whole page. */
  titleAs?: 'div' | 'h1' | 'h2' | 'h3'
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
      {icon && <div className="mb-1 flex size-12 items-center justify-center rounded-2xl bg-mist text-forest">{icon}</div>}
      <Title className="text-[15px] font-bold">{title}</Title>
      {children && <div className="max-w-md text-[13.5px] leading-relaxed text-muted">{children}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

export type BannerTone = 'info' | 'warn' | 'error' | 'ok'

export function Banner({ tone = 'info', title, children, action, className }: { tone?: BannerTone; title?: ReactNode; children?: ReactNode; action?: ReactNode; className?: string }) {
  const styles: Record<BannerTone, string> = {
    info: 'bg-mist text-text',
    warn: 'bg-warn-soft text-gold-deep',
    error: 'bg-live-soft text-live',
    ok: 'bg-ok-soft text-ok',
  }
  const Icon = tone === 'warn' ? AlertTriangle : tone === 'error' ? XCircle : tone === 'ok' ? CheckCircle2 : Info
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={cx('flex items-start gap-3 rounded-xl px-4 py-3 text-[13.5px] leading-snug', styles[tone], className)}>
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {title && <div className="font-bold">{title}</div>}
        {children && <div className={title ? 'opacity-90' : ''}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

/**
 * Scrollable table container; use with className="data-table" on the table.
 * `relative` makes it the containing block of absolutely positioned content in the cells (such as
 * sr-only header labels), so that content is clipped with the table instead of widening the page.
 */
export function TableWrap({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('card relative overflow-x-auto px-2 pb-2 sm:px-4', className)}>{children}</div>
}

export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
  label,
}: {
  value: T
  onChange: (v: T) => void
  tabs: { value: T; label: ReactNode; count?: number }[]
  label: string
}) {
  const listRef = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ start: false, end: false })

  // Tabs scroll sideways on phones: keep the selected tab in view and fade the edge that hides more.
  useEffect(() => {
    const list = listRef.current
    if (!list) return
    const update = () => {
      const max = list.scrollWidth - list.clientWidth
      setEdges({ start: list.scrollLeft > 2, end: list.scrollLeft < max - 2 })
    }
    update()
    list.addEventListener('scroll', update, { passive: true })
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null
    ro?.observe(list)
    return () => {
      list.removeEventListener('scroll', update)
      ro?.disconnect()
    }
  }, [])
  useEffect(() => {
    const list = listRef.current
    const active = list?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!list || !active) return
    const left = active.offsetLeft
    if (left < list.scrollLeft) list.scrollLeft = left - 16
    else if (left + active.offsetWidth > list.scrollLeft + list.clientWidth) list.scrollLeft = left + active.offsetWidth - list.clientWidth + 16
  }, [value])

  // Arrow keys, Home and End move between tabs (only the selected tab is in the Tab order).
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex((t) => t.value === value)
    let next = -1
    if (e.key === 'ArrowRight') next = (i + 1) % tabs.length
    else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = tabs.length - 1
    if (next < 0 || !tabs[next]) return
    e.preventDefault()
    onChange(tabs[next].value)
    listRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus()
  }

  const fade =
    edges.start && edges.end
      ? '[mask-image:linear-gradient(to_right,transparent,black_28px,black_calc(100%_-_28px),transparent)]'
      : edges.end
        ? '[mask-image:linear-gradient(to_right,black_calc(100%_-_28px),transparent)]'
        : edges.start
          ? '[mask-image:linear-gradient(to_right,transparent,black_28px)]'
          : ''

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cx('relative flex gap-1 overflow-x-auto border-b border-line [scrollbar-width:none]', fade)}
    >
      {tabs.map((t) => {
        const active = t.value === value
        return (
          <button
            key={t.value}
            role="tab"
            type="button"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(t.value)}
            className={cx(
              '-mb-px min-h-11 shrink-0 border-b-[3px] px-4 text-[14px] whitespace-nowrap transition focus-visible:-outline-offset-2',
              active ? 'border-forest font-bold text-forest' : 'border-transparent font-medium text-muted hover:text-text',
            )}
          >
            {t.label}
            {t.count !== undefined && <span className="num ml-1.5 text-[12px] opacity-70">{t.count}</span>}
          </button>
        )
      })}
    </div>
  )
}

/** Definition-list row for detail panels. */
export function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="label-caps">{label}</dt>
      <dd className="text-[14px]">{children || <span className="text-muted">—</span>}</dd>
    </div>
  )
}
